/**
 * `minimum_rest_between_shifts`: too little rest between this shift and the
 * guard's previous or next one. Either someone else works this shift, or the
 * guard starts later / finishes earlier by the shortfall and the released time
 * is handed over (or left open, which the board then shows as a gap).
 */

import {
  findViolationShift,
  numberDetail,
  restGapSide,
  snapDown,
  snapUp,
} from '../rosterModel';
import type { RemediationStrategy, StrategyResult } from '../types';
import { reassignShiftProposals, releaseShiftProposals } from './fixBuilders';

export const restPeriodStrategy: RemediationStrategy = {
  ruleCodes: ['minimum_rest_between_shifts'],

  async propose(violation, context): Promise<StrategyResult> {
    const shift = findViolationShift(violation, context.snapshot);
    if (!shift) return { proposals: [], unsupportedReason: 'shift_not_on_roster' };

    const proposals = await reassignShiftProposals(shift, context, { limit: 3, preference: 0 });

    const requiredRest = numberDetail(violation, 'required_rest_minutes') ?? context.rule?.minimumRestMinutes ?? null;
    const actualRest = numberDetail(violation, 'rest_minutes');
    if (requiredRest !== null && actualRest !== null) {
      const shortfall = requiredRest - actualRest;
      const gapBefore = restGapSide(violation, context.snapshot.site.id) === 'before';
      proposals.push(...await releaseShiftProposals(shift, context, {
        side: gapBefore ? 'start' : 'end',
        boundaryMinute: gapBefore ? snapUp(shift.startMinute + shortfall) : snapDown(shift.endMinute - shortfall),
        handoverLimit: 2,
        preference: 10,
      }));
    }

    return proposals.length > 0 ? { proposals } : { proposals, unsupportedReason: 'no_eligible_guards' };
  },
};
